import { useEffect, useState } from "react";
import client from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Terminal, Plus, Copy, Check, Trash2, AlertTriangle, Loader2 } from "lucide-react";

/**
 * External API tokens card — long-lived non-expiring keys used for server-to-server
 * calls against `/api/external/*` endpoints. The raw token is displayed exactly once
 * at creation time; only its 12-char prefix is persisted for later identification.
 */
export function ApiTokensCard() {
  const [items, setItems] = useState(null);
  const [showRevoked, setShowRevoked] = useState(false);
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState(null);

  const refresh = () =>
    client
      .get("/api-tokens")
      .then((r) =>
        setItems(
          [...r.data].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")),
        ),
      )
      .catch(() => setItems([]));

  useEffect(() => {
    refresh();
  }, []);

  const revoke = async () => {
    if (!revoking) return;
    try {
      await client.delete(`/api-tokens/${revoking.id}`);
      toast.success("Token revoked");
      setRevoking(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revoke failed");
    }
  };

  const visible = (items || []).filter((t) => showRevoked || !t.revoked);

  return (
    <Card className="mb-6 p-6" data-testid="settings-api-tokens-card">
      <div className="mb-1 flex items-center gap-2">
        <Terminal className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-lg font-semibold">External API tokens</h2>
      </div>
      <p className="mb-4 text-xs text-muted-foreground">
        Long-lived keys for server-to-server integrations. Use as{" "}
        <code className="rounded bg-muted px-1 py-0.5 font-mono text-[11px]">X-API-Key</code> header on{" "}
        <code className="rounded bg-muted px-1 py-0.5 font-mono text-[11px]">/api/external/*</code> endpoints.
        Tokens do not expire — revoke unused ones.
      </p>

      <div className="mb-3 flex items-center justify-between gap-2">
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <input
            type="checkbox"
            data-testid="show-revoked-toggle"
            checked={showRevoked}
            onChange={(e) => setShowRevoked(e.target.checked)}
          />
          Show revoked
        </label>
        <Button size="sm" data-testid="new-api-token-btn" onClick={() => setCreating(true)} className="gap-1.5">
          <Plus className="h-3.5 w-3.5" /> New token
        </Button>
      </div>

      {!items && <Skeleton className="h-24" />}
      {items && visible.length === 0 && (
        <div
          className="rounded-md border border-dashed border-border p-6 text-center text-xs text-muted-foreground"
          data-testid="api-tokens-empty"
        >
          No API tokens yet. Create one to start integrating an external system.
        </div>
      )}
      {items && visible.length > 0 && (
        <div className="overflow-hidden rounded-md border border-border">
          <table className="min-w-full text-sm" data-testid="api-tokens-table">
            <thead className="thead">
              <tr>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Prefix</th>
                <th className="px-3 py-2 text-left">Created</th>
                <th className="px-3 py-2 text-left">Last used</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {visible.map((t) => (
                <tr
                  key={t.id}
                  className={`border-t border-border ${t.revoked ? "opacity-50" : ""}`}
                  data-testid={`api-token-row-${t.id}`}
                >
                  <td className="px-3 py-2 font-medium">{t.name}</td>
                  <td className="px-3 py-2 font-mono text-xs">{t.token_prefix}…</td>
                  <td className="px-3 py-2 font-mono text-[10px] text-muted-foreground">
                    {t.created_at?.slice(0, 16).replace("T", " ")}
                  </td>
                  <td className="px-3 py-2 font-mono text-[10px] text-muted-foreground">
                    {t.last_used_at ? t.last_used_at.slice(0, 16).replace("T", " ") : "never"}
                  </td>
                  <td className="px-3 py-2">
                    {t.revoked ? (
                      <span className="rounded-full border border-border bg-muted px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
                        revoked
                      </span>
                    ) : (
                      <span className="rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest text-emerald-700 dark:text-emerald-400">
                        active
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {!t.revoked && (
                      <Button
                        size="sm"
                        variant="ghost"
                        data-testid={`revoke-token-btn-${t.id}`}
                        onClick={() => setRevoking(t)}
                        title="Revoke"
                      >
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateTokenDialog open={creating} onOpenChange={setCreating} onCreated={refresh} />

      <AlertDialog open={!!revoking} onOpenChange={(o) => !o && setRevoking(null)}>
        <AlertDialogContent data-testid="revoke-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke &quot;{revoking?.name}&quot;?</AlertDialogTitle>
            <AlertDialogDescription>
              Any integration using this token will start receiving <code>401</code>. This cannot be undone —
              create a new token to restore access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={revoke}
              data-testid="confirm-revoke-btn"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

function CreateTokenDialog({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) {
      setName("");
      setResult(null);
      setCopied(false);
    }
  }, [open]);

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await client.post("/api-tokens", { name: name.trim() });
      setResult(data);
      onCreated();
      toast.success("Token created");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(result.token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked */
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="create-token-dialog">
        <DialogHeader>
          <DialogTitle>New API token</DialogTitle>
          <DialogDescription>
            Give this token a memorable name so you can identify it later (e.g. &quot;CMS-prod&quot;, &quot;etl-job&quot;).
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div>
            <Label className="data-label mb-1 block">Token name</Label>
            <Input
              data-testid="new-token-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="CMS-prod"
              autoFocus
            />
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex gap-3 rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-xs">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <p>
                This token will not be shown again. Copy it now and store it in your integration&apos;s secret
                manager.
              </p>
            </div>
            <div className="flex items-center gap-2 rounded-md border border-border bg-background p-2">
              <code className="flex-1 truncate font-mono text-xs" data-testid="new-token-value">
                {result.token}
              </code>
              <Button size="sm" variant="ghost" onClick={copy} data-testid="copy-token-btn">
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Use with header{" "}
              <code className="rounded bg-muted px-1 py-0.5 font-mono">X-API-Key: {result.token_prefix}…</code>
            </p>
          </div>
        )}

        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
                Cancel
              </Button>
              <Button
                data-testid="new-token-save"
                onClick={submit}
                disabled={!name.trim() || saving}
              >
                {saving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Plus className="mr-1.5 h-3.5 w-3.5" />}
                Create token
              </Button>
            </>
          ) : (
            <Button onClick={() => onOpenChange(false)} data-testid="create-token-done">Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
